import type { Card } from "@amzn/ads-blink-client";
import { SortOptions } from "@amzn/ads-blink-client";
import type { Content } from "@amzn/ads-discovery-theme-service-typescript-client";
import { SalesforceObjectType } from "@amzn/ads-measurement-plm-service-type-script-client";
import type { TemplateAssociations } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import type { InsightFamily } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import { useStore } from "zustand";

import { initialUnifiedFilterState } from "../../../../config/filters/unified";
import type { UnifiedContent } from "../../../../hooks/insightPackages/useInsightPackagesLoader";
import { useUnifiedContentsLoader } from "../../../../hooks/unifiedContent/useUnifiedContentsLoader";
import type { AssociatedPitchedContent } from "../../../../store/editContent";
import {
    FeatureFlags,
    FeatureFlagsStore,
} from "../../../../store/featureFlags";
import { unifiedFilterStore } from "../../../../store/unifiedFilters";
import { InsightFamilyName } from "../../../../types/providers";
import { isSuccess } from "../../../../utils/api.utils";
import { OpportunityStatusText } from "../../../../utils/salesforce.utils";
import {
    InsightFamilyCard,
    InsightFamilyCardV2,
} from "../insightFamilies/InsightFamilyCard";
import { StandaloneRecommendationCard } from "../insightPackages/StandaloneRecommendationCard";
import { MarketingNarrativeCard } from "../marketingNarratives/MarketingNarrativeCard";
import { OnDemandInsightCard } from "../onDemandInsights/OnDemandInsightsCard";
import { PitchedContentCard } from "../pitchedContents/PitchedContentsCard";
import { withInsightsRenderer } from "../withInsightsRenderer";

import { UnifiedContentCard } from "./UnifiedContentCard";

interface UnifiedContentsProps {
    isLoaderInView: boolean;
    setAsyncStatus: Dispatch<SetStateAction<AsyncStatus>>;
    setTotalCount: Dispatch<SetStateAction<number>>;
}

export const UnifiedContents: React.FunctionComponent<UnifiedContentsProps> = (
    props: UnifiedContentsProps,
) => {
    const { isLoaderInView, setAsyncStatus, setTotalCount } = props;
    const [asyncStatus, lazyLoadedInsights, totalResults] =
        useUnifiedContentsLoader({
            isLoaderInView,
        });

    const [isUnifiedSearchSortDropdown] = useStore(
        FeatureFlagsStore,
        (state) => [state[FeatureFlags.UNIFIED_SEARCH_SORT_DROPDOWN]],
    );

    const { setSortAttribute } = unifiedFilterStore.getState();

    useEffect(() => {
        setAsyncStatus(asyncStatus);
    }, [asyncStatus]);

    useEffect(() => {
        if (isSuccess(asyncStatus)) {
            setTotalCount(totalResults);
        }
    }, [asyncStatus, totalResults]);

    useEffect(() => {
        if (isUnifiedSearchSortDropdown) {
            setSortAttribute(SortOptions.LAST_MODIFIED_DATE);
        } else {
            setSortAttribute(SortOptions.TYPE);
        }
    }, [isUnifiedSearchSortDropdown, setSortAttribute]);

    /**
     * Until we can reach UX alignment on using a unified card layout, we need to convert
     * the Cerebro responses back to the current format and layout
     */
    const convertCardToRecommendation = (content: Card): UnifiedContent => {
        const details = content.details;
        return {
            identifier: content.identifier,
            browseNode: content.subtitle,
            brand: content.title,
            title: content.description,
            insightTypeName: content.insightType,
            metadata: {
                description: content.description ?? null,
                entityId: details?.entityId ?? null,
                entityName: details?.entityName ?? null,
                linkedApbs: Number(details?.linkedApbs) || null,
                entityType: details?.entityType ?? null,
                entityTypeId: details?.entityId ?? null,
                apbRules: details?.apbRules ?? null,
            },
        };
    };

    /**
     * Until we can reach UX alignment on using a unified card layout, we need to convert
     * the Cerebro responses back to the current format and layout
     */
    const convertCardToTemplate = (content: Card): TemplateAssociations => {
        return {
            id: content.identifier,
            name: content.title,
            description: content.description,
            language: content.footer,
            program: {
                themes: [content.header ?? ""],
                name: content.subtitle,

                lastUpdatedUser: undefined,
                lastUpdatedTimestamp: undefined,
                id: undefined,
            },
            domain: {
                id: content.details?.domainId,

                lastUpdatedUser: undefined,
                lastUpdatedTimestamp: undefined,
                name: undefined,
                metadata: undefined,
            },
            lastUpdatedUser: undefined,
            lastUpdatedTimestamp: undefined,
        };
    };

    /**
     * Until we can reach UX alignment on using a unified card layout, we need to convert
     * the Cerebro responses back to the current format and layout
     */
    const convertCardToInsightFamily = (content: Card): InsightFamily => {
        return {
            identifier: content.identifier,
            title: content.title,
            advertiserId: undefined,
            advertiser: undefined,
            familyName: content.header,
            metadata: content.details ? content.details.metadata : undefined,
            brand: content.footer,
            browseNode: content.subtitle,
            insightFamilyTrackingId:
                content.details?.metadata?.["familyTrackingId"] ?? undefined,
        };
    };

    const convertCardtoMarketingContent = (content: Card): Content => {
        const metadata = content.details?.metadata ?? {};
        return {
            contentId: metadata["contentId"],
            contentTitle: metadata["contentTitle"],
            contentCategoryIds: [],
            lastModifiedDate: metadata["lastModifiedDate"],
            totalViews: +metadata["totalViews"],
            totalDownloads: +metadata["totalDownloads"],
        } as Content;
    };

    const convertCardToPitchedContent = (
        content: Card,
    ): AssociatedPitchedContent => {
        const metadata = content.details?.metadata ?? {};
        const opportunityId = metadata?.["salesforceOpportunity"]?.["id"]?.[0];
        const activityId = metadata?.["salesforceActivity"]?.["id"]?.[0];
        const salesforceObjectType: SalesforceObjectType = opportunityId
            ? SalesforceObjectType.OPPORTUNITY
            : activityId
              ? SalesforceObjectType.ACTIVITY
              : SalesforceObjectType.ACCOUNT_ONLY;
        const activityName =
            metadata?.["salesforceActivity"]?.["displayName"]?.[0];
        const activityDate = metadata?.["activityDate"]?.["displayName"]?.[0];
        const accountName =
            metadata?.["salesforceAccount"]?.["displayName"]?.[0];
        const opportunityName =
            metadata?.["salesforceOpportunity"]?.["displayName"]?.[0];
        const opportunityStatus =
            metadata?.["opportunityStatus"]?.["displayName"]?.[0];

        return {
            contentId: content.identifier,
            contentTitle: content.title,
            creationDate: metadata?.["creationDate"],
            lastModifiedDate: content.footer,
            fileFormat: metadata?.["fileFormat"],
            contentNote: metadata?.["contentNote"],
            accountOpportunities: [
                {
                    accountName:
                        metadata?.["salesforceAccount"]?.["displayName"]?.[0],
                    opportunityName,
                    opportunityStatus: Object.keys(OpportunityStatusText).find(
                        (key) =>
                            OpportunityStatusText[key] === opportunityStatus,
                    ),
                    totalAmount:
                        metadata?.["totalRevenue"]?.["displayName"]?.[0],
                    currencyIsoCode:
                        metadata?.["currencyISOCode"]?.["displayName"]?.[0],
                },
            ],
            associations: [
                {
                    salesforceAssociation: {
                        salesforceObjectType,
                    },
                    salesforceActivityMetadata: {
                        activityName:
                            [activityName, activityDate]
                                .filter((x) => x)
                                .join(" | ") || undefined,
                    },
                    salesforceAccountMetadata: {
                        accountName,
                    },
                },
            ],
        } as AssociatedPitchedContent;
    };

    /**
     * InsightType to provider mapping defined in the model package here
     * https://tiny.amazon.com/esrdiz63/codeamazpackD16Gblob1293mode
     */
    const getCardForContent = (content: Card, index: number) => {
        const insightType = content.insightType;
        switch (insightType) {
            case InsightType.CDB_TEMPLATE:
                return (
                    <OnDemandInsightCard
                        template={convertCardToTemplate(content)}
                        key={index}
                        index={index}
                        additionalCardDetails={[
                            {
                                label: "Content Type",
                                value: "Packaged Insight",
                            },
                        ]}
                        tag={
                            content.header === "Ai Insights"
                                ? "Pilot"
                                : undefined
                        }
                    />
                );
            case InsightType.PACKAGING:
                return (
                    <StandaloneRecommendationCard
                        recommendation={convertCardToRecommendation(content)}
                        key={index}
                        index={index}
                        additionalCardDetails={[
                            {
                                label: "Content Type",
                                value: "Packaged Insight",
                            },
                        ]}
                    />
                );
            case InsightType.INSIGHT: {
                const insight = convertCardToInsightFamily(content);
                // See: https://t.corp.amazon.com/V1870390337
                // TODO: We have upcoming work to migrate everyone to the v2 layout, and we will address this conditional check then
                if (insight.familyName === InsightFamilyName.INSIGHT_GENIE) {
                    insight.tag = "Pilot";
                    return (
                        <InsightFamilyCard
                            insight={insight}
                            key={index}
                            index={index}
                        />
                    );
                } else {
                    return (
                        <InsightFamilyCardV2
                            insight={insight}
                            key={index}
                            index={index}
                        />
                    );
                }
            }
            case InsightType.MARKETING: {
                const metadata = content.details?.metadata ?? {};
                return (
                    <MarketingNarrativeCard
                        key={index}
                        index={index}
                        content={convertCardtoMarketingContent(content)}
                        contentUser={metadata["contentUsers"] ?? []}
                        unifiedCard={content}
                    />
                );
            }
            case InsightType.PITCHED_CONTENT: {
                const pitchedContent: AssociatedPitchedContent =
                    convertCardToPitchedContent(content);
                return (
                    <PitchedContentCard
                        key={`pitched-${index}-${pitchedContent.contentId}`}
                        content={pitchedContent}
                        isEditable={false}
                        isSharable={false}
                    />
                );
            }
            default:
                console.error(`Unsupported content type: ${insightType}`);
                return <></>;
        }
    };

    const getCardForContentV2 = (content: Card, index: number) => {
        const filterConfig =
            initialUnifiedFilterState.filters.UNIFIED_TYPE.find(
                (filter) => filter.id === content.header,
            );
        const tag = filterConfig?.tag?.label;
        return <UnifiedContentCard content={content} index={index} tag={tag} />;
    };

    return (
        <>
            {lazyLoadedInsights.map(
                isUnifiedSearchSortDropdown
                    ? getCardForContentV2
                    : getCardForContent,
            )}
            {asyncStatus === "error" && <div>Failed to load contents</div>}
        </>
    );
};

export const UnifiedContentsViewer = withInsightsRenderer(UnifiedContents, {
    showSearchSortDropdown: true,
});
